import { Injectable, Injector, signal, inject, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, firstValueFrom, Subscription } from 'rxjs';
import { CallState, CallSession, CallError } from '../../types/telnyx-webrtc';
import { TelnyxRTC, TelnyxCall, TelnyxNotification } from '@telnyx/webrtc';
import { environment } from '../../environments/environment';
import { ApiBaseService } from './api.base.service';
import { authService } from './auth.service';
import { WebRTCErrorHandlerService } from './webrtc-error-handler.service';
import { TelnyxConnectionService, TelnyxCredentials } from './telnyx/telnyx-connection.service';
import { TelnyxAudioService } from './telnyx/telnyx-audio.service';
import { TelnyxDiagnosticsService } from './telnyx/telnyx-diagnostics.service';
import { PhoneNumberService } from './telnyx/phone-number.service';
import { TELNYX_CONSTANTS } from './telnyx/telnyx.constants';
import { NotificationsService } from './notifications/notifications.service';

@Injectable({
  providedIn: 'root',
})
export class TelnyxWebRTCService extends ApiBaseService implements OnDestroy {
  // Injected services
  private connectionService = inject(TelnyxConnectionService);
  private audioService = inject(TelnyxAudioService);
  private diagnosticsService = inject(TelnyxDiagnosticsService);
  private phoneService = inject(PhoneNumberService);

  // Call state signals (owned by this service)
  private _currentCall = signal<TelnyxCall | null>(null);
  private _callState = signal<CallState>('idle');
  private _answerInProgress = signal(false);
  private _isMuted = signal(false);

  // Grace period after answering to prevent premature hangup processing
  private _answerGracePeriodActive = false;
  private _answerGraceTimer: ReturnType<typeof setTimeout> | null = null;
  private _lastAnswerTime = 0;

  // Ring timeout for outbound calls (hang up if not answered)
  private _ringTimeoutTimer: ReturnType<typeof setTimeout> | null = null;

  // Event subjects
  private callEventsSubject = new Subject<{ event: string; data: any }>();
  private errorSubject = new Subject<CallError>();

  // Call session management
  private activeCalls = new Map<string, CallSession>();

  // Configuration
  private companyPhoneNumber: string | null = null;
  private isInitialized = false;
  private isInitializing = false;
  private authSubscription: Subscription | null = null;
  private connectionSubscription: Subscription | null = null;

  // Reconnection state
  private isRefreshingCredentials = false;
  private visibilityChangeHandler: (() => void) | null = null;
  private lastVisibilityCheck = 0;
  private readonly VISIBILITY_CHECK_DEBOUNCE_MS = 2000;

  constructor(
    private http: HttpClient,
    private authService: authService,
    private errorHandler: WebRTCErrorHandlerService,
    private injector: Injector
  ) {
    super(http, '/calls');

    // Initialize WebRTC on authentication
    // BehaviorSubject emits current value immediately, so no separate init call needed
    this.authSubscription = this.authService.authState$.subscribe(isAuthenticated => {
      console.log('[Telnyx] authState fired:', isAuthenticated, 'role:', this.authService.getUserRole());

      if (isAuthenticated && !this.isInitialized && !this.isInitializing) {
        const role = this.authService.getUserRole();
        if (role === 'LEAD') return;
        this.initializeWebRTC();
      } else if (!isAuthenticated) {
        this.cleanup();
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  // ============================================
  // Public Getters (Delegated to ConnectionService)
  // ============================================

  get connected() {
    return this.connectionService.connected;
  }

  get connectionStatus() {
    return this.connectionService.connectionStatus;
  }

  get currentCall() {
    return this._currentCall;
  }

  get callState() {
    return this._callState;
  }

  get isMuted() {
    return this._isMuted;
  }

  // ============================================
  // Observable Streams
  // ============================================

  getCallEvents(): Observable<{ event: string; data: any }> {
    return this.callEventsSubject.asObservable();
  }

  getConnectionEvents(): Observable<{ event: string; data?: any }> {
    return this.connectionService.connectionEvents$;
  }

  getErrors(): Observable<CallError> {
    return this.errorSubject.asObservable();
  }

  // ============================================
  // Call Operations
  // ============================================

  async makeCall(phoneNumber: string, leadId?: number): Promise<TelnyxCall> {
    const client = this.connectionService.getClient();
    if (!client || !this.connectionService.connected()) {
      throw new Error('WebRTC client not connected');
    }

    const formattedNumber = this.phoneService.formatToE164(phoneNumber);
    if (!formattedNumber) {
      throw new Error(`Invalid phone number format: ${phoneNumber}`);
    }

    // Validate microphone access
    const { canCall, reason } = await this.diagnosticsService.canMakeCalls();
    if (!canCall) {
      throw new Error(reason);
    }

    try {
      this._callState.set('dialing');

      // Create backend call record
      const backendCallResponse = await this.initiateCallViaBackend(formattedNumber, leadId);
      const backendCallId = backendCallResponse.callId;

      // Create WebRTC call with custom header for backend call ID
      const clientStateData = leadId ? { leadId, backendCallId } : { backendCallId };
      const call = client.newCall({
        destinationNumber: formattedNumber,
        callerNumber: this.companyPhoneNumber || environment.companyPhoneNumber || undefined,
        clientState: btoa(JSON.stringify(clientStateData)),
        customHeaders: [
          { name: 'X-Backend-Call-Id', value: String(backendCallId) },
        ],
        
      });

      this._currentCall.set(call);

      // Start audio stream polling for early media (ringback tone)
      // Note: This only attaches audio - state changes happen in handleCallActive()
      this.audioService.startStreamPolling(call, () => this.handleEarlyMedia(call));

      // Create call session
      this.createCallSession(call, 'outbound', leadId, phoneNumber, backendCallId);

      this.callEventsSubject.next({
        event: TELNYX_CONSTANTS.EVENTS.CALL_INITIATED,
        data: { call, phoneNumber, leadId, backendCallId },
      });

      // Start ring timeout timer - hang up if not answered within timeout
      this.startRingTimeout(call);

      (call as any).backendCallId = backendCallId;
      return call;
    } catch (error) {
      this._callState.set('failed');
      this.emitError({
        code: TELNYX_CONSTANTS.ERROR_CODES.CALL_FAILED,
        message: 'Failed to initiate call',
        details: error,
      });
      throw error;
    }
  }

  answerCall(): boolean {
    if (this._answerInProgress() || this._callState() === 'connecting') {
      return false;
    }

    const currentCall = this._currentCall();
    if (!currentCall) {
      this.emitError({
        code: TELNYX_CONSTANTS.ERROR_CODES.NO_ACTIVE_CALL,
        message: 'No incoming call found to answer',
        details: null,
      });
      return false;
    }

    if (!['ringing', 'connecting'].includes(currentCall.state)) {
      this.emitError({
        code: TELNYX_CONSTANTS.ERROR_CODES.INVALID_CALL_STATE,
        message: `Cannot answer call in state: ${currentCall.state}`,
        details: { callState: currentCall.state },
      });
      return false;
    }

    try {
      this._answerInProgress.set(true);
      this._callState.set('connecting');

      // Start answer grace period to prevent premature hangup notifications
      this._answerGracePeriodActive = true;
      this._lastAnswerTime = Date.now();

      if (currentCall.answer && typeof currentCall.answer === 'function') {
        currentCall.answer();
      }

      // Clear grace period after 5 seconds (enough time for call to stabilize)
      if (this._answerGraceTimer) {
        clearTimeout(this._answerGraceTimer);
      }
      this._answerGraceTimer = setTimeout(() => {
        this._answerGracePeriodActive = false;
      }, 500);

      // Clear flag after delay
      setTimeout(() => {
        this._answerInProgress.set(false);
      }, TELNYX_CONSTANTS.TIMEOUTS.ANSWER_FLAG_CLEAR_MS);

      return true;
    } catch (error) {
      this._answerInProgress.set(false);
      this._callState.set('failed');
      this.emitError({
        code: TELNYX_CONSTANTS.ERROR_CODES.ANSWER_FAILED,
        message: 'Failed to answer call',
        details: error,
      });
      return false;
    }
  }

  hangupCall(): void {
    const currentCall = this._currentCall();
    if (currentCall) {
      try {
        currentCall.hangup();
        this._callState.set('ending');
        this._currentCall.set(null);
      } catch (error) {
        this.emitError({
          code: TELNYX_CONSTANTS.ERROR_CODES.HANGUP_FAILED,
          message: 'Failed to hang up call',
          details: error,
        });
      }
    }
  }

  toggleHold(): void {
    const currentCall = this._currentCall();
    // Allow toggle when call is active OR already held (to unhold)
    if (currentCall && (currentCall.state === 'active' || currentCall.state === 'held')) {
      try {
        if (this._callState() === 'held' || currentCall.state === 'held') {
          currentCall.unhold();
          this._callState.set('connected');
        } else {
          currentCall.hold();
          this._callState.set('held');
        }
      } catch (error) {
        this.emitError({
          code: TELNYX_CONSTANTS.ERROR_CODES.HOLD_FAILED,
          message: 'Failed to toggle hold',
          details: error,
        });
      }
    }
  }

  toggleMute(): boolean {
    const currentCall = this._currentCall();

    // Allow mute when call is active OR held
    if (currentCall && (currentCall.state === 'active' || currentCall.state === 'held')) {
      try {
        const currentlyMuted = this._isMuted();

        // Telnyx SDK uses muteAudio/unmuteAudio or we can control audio tracks directly
        if (currentlyMuted) {
          // Try SDK method first, fallback to audio track manipulation
          if (typeof (currentCall as any).unmuteAudio === 'function') {
            (currentCall as any).unmuteAudio();
          } else if (currentCall.localStream) {
            const audioTrack = currentCall.localStream.getAudioTracks()[0];
            if (audioTrack) {
              audioTrack.enabled = true;
            }
          }
          this._isMuted.set(false);
        } else {
          // Try SDK method first, fallback to audio track manipulation
          if (typeof (currentCall as any).muteAudio === 'function') {
            (currentCall as any).muteAudio();
          } else if (currentCall.localStream) {
            const audioTrack = currentCall.localStream.getAudioTracks()[0];
            if (audioTrack) {
              audioTrack.enabled = false;
            }
          }
          this._isMuted.set(true);
        }

        return !currentlyMuted;
      } catch (error) {
        this.emitError({
          code: TELNYX_CONSTANTS.ERROR_CODES.MUTE_FAILED,
          message: 'Failed to toggle mute',
          details: error,
        });
        return false;
      }
    }

    return false;
  }

  sendDTMF(digits: string): void {
    const currentCall = this._currentCall();
    if (currentCall && currentCall.state === 'active') {
      try {
        currentCall.dtmf(digits);
        this.callEventsSubject.next({
          event: TELNYX_CONSTANTS.EVENTS.DTMF_SENT,
          data: { digits, callId: currentCall.id },
        });
      } catch (error) {
        this.emitError({
          code: TELNYX_CONSTANTS.ERROR_CODES.DTMF_FAILED,
          message: 'Failed to send DTMF',
          details: error,
        });
      }
    }
  }

  // ============================================
  // Audio Operations (Delegated)
  // ============================================

  async getAudioDevices() {
    return this.audioService.getAudioDevices();
  }

  setAudioDevices(microphoneId?: string, speakerId?: string): void {
    const client = this.connectionService.getClient();
    this.audioService.setAudioDevices(client, microphoneId, speakerId);
  }

  async triggerRemoteAudioPlayback(): Promise<void> {
    await this.audioService.triggerPlayback();
  }

  async testAudioOutput(): Promise<boolean> {
    return this.audioService.testAudioOutput();
  }

  // ============================================
  // Diagnostics (Delegated)
  // ============================================

  static isWebRTCSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      window.RTCPeerConnection
    );
  }

  async validateMicrophoneAccess(): Promise<boolean> {
    return this.diagnosticsService.validateMicrophoneAccess();
  }

  async requestMicrophonePermission(): Promise<boolean> {
    return this.diagnosticsService.requestMicrophonePermission();
  }

  async runConnectionDiagnostics() {
    const result = await this.diagnosticsService.runDiagnostics();
    return {
      browserSupport: result.browserSupport,
      permissions: result.permissions,
      networkConnectivity: result.networkConnectivity,
      backendCredentials: true, // We'll check this separately
      iceServerConnectivity: result.iceServerConnectivity,
      telnyxConnection: this.connectionService.connected(),
      diagnosticDetails: result.details,
    };
  }

  getWebRTCConnectionInfo() {
    return {
      telnyxConnected: this.connectionService.connected(),
      telnyxStatus: this.connectionService.connectionStatus(),
    };
  }

  // ============================================
  // Session Management
  // ============================================

  getCurrentCallSession(): CallSession | null {
    const currentCall = this._currentCall();
    return currentCall ? this.activeCalls.get(currentCall.id) || null : null;
  }

  // ============================================
  // Polling (for external use)
  // ============================================

  startStreamPolling(call: TelnyxCall): void {
    this.audioService.startStreamPolling(call, () => {
      this.handleCallConnected(call);
    });
  }

  // ============================================
  // Private Methods
  // ============================================

  private async initializeWebRTC(): Promise<void> {
    if (this.isInitialized || this.isInitializing) {
      return;
    }

    this.isInitializing = true;

    try {
      // Check browser support
      if (!TelnyxWebRTCService.isWebRTCSupported()) {
        throw new Error('WebRTC is not supported in this browser');
      }

      // Request microphone permission
      const micGranted = await this.diagnosticsService.requestMicrophonePermission();
      if (!micGranted) {
        throw new Error('Microphone permissions are required for calling functionality.');
      }

      // Fetch credentials from backend
      const credentialsResponse = await firstValueFrom(
        this.get('', 'webrtc-credentials')
      );

      const credentials: TelnyxCredentials = {
        login: credentialsResponse.data.username,
        password: credentialsResponse.data.password,
        domain: credentialsResponse.data.domain || TELNYX_CONSTANTS.SIP.DEFAULT_DOMAIN,
        realm: credentialsResponse.data.realm || TELNYX_CONSTANTS.SIP.DEFAULT_REALM,
      };

      // Fetch company phone number
      try {
        const phoneResponse = await firstValueFrom(
          this.get('', 'company-phone-number')
        );
        this.companyPhoneNumber = phoneResponse.phoneNumber;
      } catch {
        this.companyPhoneNumber = environment.companyPhoneNumber || null;
      }

      // Connect via connection service
      await this.connectionService.connect(credentials);

      // Subscribe to connection events for call notifications and auth errors
      if (this.connectionSubscription) {
        this.connectionSubscription.unsubscribe();
      }
      this.connectionSubscription = this.connectionService.connectionEvents$.subscribe(event => {
        if (event.event === 'ready') {
          this.setupNotificationHandler();
        }

        if (event.event === 'auth-error') {
          this.refreshCredentialsAndReconnect();
        }
      });

      // Setup visibility change listener for wake-from-sleep detection
      this.setupVisibilityChangeListener();

      // Setup notification handler if already connected
      if (this.connectionService.connected()) {
        this.setupNotificationHandler();
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('[TelnyxWebRTC] WebRTC initialization failed:', error);
      this.emitError({
        code: TELNYX_CONSTANTS.ERROR_CODES.INIT_FAILED,
        message: 'Failed to initialize WebRTC client',
        details: error,
      });
    } finally {
      this.isInitializing = false;
    }
  }

  private setupNotificationHandler(): void {
    this.connectionService.onNotification((notification: TelnyxNotification) => {
      this.handleTelnyxNotification(notification);
    });
  }

  private handleTelnyxNotification(notification: TelnyxNotification): void {
    if (notification.type === 'callUpdate' && notification.call) {
      const call = notification.call as TelnyxCall;

      console.log("[klajdi]", call.state, "at", call);

      // Handle call ended - but respect answer grace period
      if (['hangup', 'destroy', 'purge'].includes(call.state)) {
        // During grace period after answering, ignore premature hangup notifications
        // This prevents race conditions where the backend briefly reports hangup before bridge completes
        if (this._answerGracePeriodActive) {
          // If the call is actually active, don't end it
          if (this._callState() === 'connected' || this._callState() === 'connecting') {
            return;
          }
        }
        this.handleCallEnded(call);
      }

      // Handle incoming call
      if (call.state === 'ringing') {
        this.handleIncomingCall(call);
      }

      // Handle call active - this should end the grace period early
      if (call.state === 'active') {
        // Call is confirmed active, we can end grace period early
        if (this._answerGracePeriodActive) {
          this._answerGracePeriodActive = false;
          if (this._answerGraceTimer) {
            clearTimeout(this._answerGraceTimer);
            this._answerGraceTimer = null;
          }
        }
        this.handleCallActive(call);
      }
    }

    this.callEventsSubject.next({
      event: TELNYX_CONSTANTS.EVENTS.NOTIFICATION,
      data: notification,
    });
  }

  private handleIncomingCall(call: TelnyxCall): void {
    // Extract lead info from custom headers
    let leadInfo: any = null;
    let callerPhone = 'Unknown';
    let dbCallId: number | undefined;
    let isLeadAlert = false;
    let sessionId: string | undefined;

    const customHeaders = call.options?.customHeaders as Array<{ name: string; value: string }> | undefined;
    if (customHeaders && Array.isArray(customHeaders)) {
      const headersMap: Record<string, string> = {};
      customHeaders.forEach(h => {
        const name = h.name.replace(/^X-/i, '');
        headersMap[name] = h.value;
      });

      // Detect Lead Alert calls
      isLeadAlert = headersMap['call_type'] === 'LEAD_ALERT';
      sessionId = headersMap['session_id'] || undefined;

      const leadId = headersMap['lead_id'];
      if (leadId) {
        leadInfo = {
          id: parseInt(leadId, 10),
          name: headersMap['lead_name'] || '',
          firstName: headersMap['lead_first'] || '',
          lastName: headersMap['lead_last'] || '',
          phone: headersMap['lead_phone'] || '',
          mobilePhone: headersMap['lead_mobile'] || '',
          email: headersMap['lead_email'] || '',
          status: headersMap['lead_status'] || '',
          campusId: headersMap['campus_id'] ? parseInt(headersMap['campus_id'], 10) : undefined,
          campusName: headersMap['campus_name'] || '',
          programId: headersMap['program_id'] ? parseInt(headersMap['program_id'], 10) : undefined,
          programName: headersMap['program_name'] || '',
          ownerId: headersMap['owner_id'] ? parseInt(headersMap['owner_id'], 10) : undefined,
          ownerName: headersMap['owner_name'] || '',
          ownerEmail: headersMap['owner_email'] || '',
        };
      }

      if (headersMap['caller_phone']) {
        callerPhone = headersMap['caller_phone'];
      }

      if (headersMap['db_call_id']) {
        dbCallId = parseInt(headersMap['db_call_id'], 10);
      }
    }

    const phoneNumber = callerPhone !== 'Unknown'
      ? callerPhone
      : call.options?.remoteCallerNumber ||
        call.options?.callerNumber ||
        call.from ||
        'Unknown';

    this._currentCall.set(call);
    this._callState.set('ringing');
    this.createCallSession(call, 'inbound');

    this.callEventsSubject.next({
      event: TELNYX_CONSTANTS.EVENTS.INCOMING_CALL,
      data: {
        call,
        telnyxCallControlId: call.options?.telnyxCallControlId,
        telnyxLegId: call.options?.telnyxLegId,
        phoneNumber,
        callId: call.id,
        leadInfo,
        dbCallId,
        isLeadAlert,
        sessionId,
      },
    });
  }

  /**
   * Handle early media (ringback tone) for outbound calls.
   * Only attaches audio stream - does NOT change call state.
   */
  private handleEarlyMedia(call: TelnyxCall): void {
    if (call.remoteStream) {
      this.audioService.attachRemoteStream(call.remoteStream);
    }
  }

  /**
   * Handle call becoming active (customer answered).
   * This is the ONLY place where outbound calls transition to 'connected'.
   */
  private handleCallActive(call: TelnyxCall): void {
    // Clear ring timeout - call actually answered (state='active')
    this.clearRingTimeout();

    if (call.remoteStream) {
      this.audioService.attachRemoteStream(call.remoteStream);
    }

    if (this._callState() !== 'connected') {
      this.handleCallConnected(call);
    }
  }

  private handleCallConnected(call: TelnyxCall): void {
    this._callState.set('connected');
    this._answerInProgress.set(false);
    this.updateCallSession(call.id, { state: 'connected', disposition: 'answered' });

    this.callEventsSubject.next({
      event: TELNYX_CONSTANTS.EVENTS.CALL_ANSWERED,
      data: { call },
    });
  }

  private handleCallEnded(call: TelnyxCall): void {
    // Clear ring timeout - call ended
    this.clearRingTimeout();

    const currentCall = this._currentCall();
    if (currentCall?.id === call.id) {
      this._currentCall.set(null);
      if (!['idle', 'ended'].includes(this._callState())) {
        this._callState.set('ended');
      }
      // Reset mute state when call ends
      this._isMuted.set(false);
      this.audioService.cleanup();
    }

    this.activeCalls.delete(call.id);

    this.callEventsSubject.next({
      event: TELNYX_CONSTANTS.EVENTS.CALL_ENDED,
      data: {
        call,
        cause: call.cause,
        causeCode: call.causeCode,
        reason: 'hangup',
      },
    });
  }

  private async initiateCallViaBackend(phoneNumber: string, leadId?: number): Promise<any> {
    if (!phoneNumber || phoneNumber.length < 10) {
      throw new Error(`Invalid phone number format: ${phoneNumber}`);
    }

    const payload = {
      to: phoneNumber,
      phoneNumber,
      leadId: leadId || null,
      source: 'webrtc',
      userId: this.authService.getCurrentUser()?.id || null,
    };

    const response = await firstValueFrom(
      this.http.post(`${environment.apiUrl}/calls/initiate`, payload)
    );

    return (response as any).data;
  }

  private createCallSession(
    call: TelnyxCall,
    direction: 'inbound' | 'outbound',
    leadId?: number,
    phoneNumber?: string,
    backendCallId?: number | string
  ): void {
    const session: CallSession = {
      id: call.id,
      leadId,
      direction,
      phoneNumber: phoneNumber || call.options?.destinationNumber || call.options?.callerNumber || 'Unknown',
      startTime: new Date(),
      state: 'ringing',
      telnyxCallId: call.id,
      backendCallId,
    };

    this.activeCalls.set(call.id, session);
  }

  private updateCallSession(callId: string, updates: Partial<CallSession>): void {
    const session = this.activeCalls.get(callId);
    if (session) {
      Object.assign(session, updates);
    }
  }

  private emitError(error: CallError): void {
    this.errorSubject.next(error);
  }

  /**
   * Start ring timeout timer for outbound calls.
   * Automatically hangs up if call is not answered within timeout.
   */
  private startRingTimeout(call: TelnyxCall): void {
    this.clearRingTimeout();

    this._ringTimeoutTimer = setTimeout(() => {
      const currentState = this._callState();
      // Only hang up if still ringing (not answered or ended)
      if (['dialing', 'ringing', 'connecting'].includes(currentState)) {
        console.log('[TelnyxWebRTC] Ring timeout - no answer, hanging up');

        this.callEventsSubject.next({
          event: TELNYX_CONSTANTS.EVENTS.CALL_TIMEOUT,
          data: { call, cause: 'no_answer' },
        });

        this.hangupCall();
      }
    }, TELNYX_CONSTANTS.TIMEOUTS.RING_TIMEOUT_MS);
  }

  /**
   * Clear the ring timeout timer.
   */
  private clearRingTimeout(): void {
    if (this._ringTimeoutTimer) {
      clearTimeout(this._ringTimeoutTimer);
      this._ringTimeoutTimer = null;
    }
  }

  /**
   * Refresh credentials from backend and reconnect.
   * Called when auth error occurs or after wake from sleep.
   *
   * This method now properly handles expired JWT tokens by:
   * 1. First attempting to refresh the JWT if expired
   * 2. Then fetching fresh WebRTC credentials
   * 3. Notifying the user if session is completely expired
   */
  private async refreshCredentialsAndReconnect(): Promise<void> {
    if (this.isRefreshingCredentials) {
      return;
    }

    this.isRefreshingCredentials = true;

    try {
      const token = this.authService.getAccessToken();
      if (!token || this.authService.isTokenExpired(token)) {
        console.log('[TelnyxWebRTC] Access token expired, session over');
        this.handleSessionExpired();
        return;
      }

      // STEP 2: Fetch fresh WebRTC credentials with valid JWT
      const credentialsResponse = await firstValueFrom(
        this.get('', 'webrtc-credentials')
      );

      const credentials: TelnyxCredentials = {
        login: credentialsResponse.data.username,
        password: credentialsResponse.data.password,
        domain: credentialsResponse.data.domain || TELNYX_CONSTANTS.SIP.DEFAULT_DOMAIN,
        realm: credentialsResponse.data.realm || TELNYX_CONSTANTS.SIP.DEFAULT_REALM,
      };

      // STEP 3: Reconnect with fresh credentials
      await this.connectionService.reconnect(credentials);
      console.log('[TelnyxWebRTC] Successfully reconnected after wake/auth-error');

    } catch (error: any) {
      console.error('[TelnyxWebRTC] Failed to refresh credentials:', error);

      // Check if this is an auth error from the API
      if (error?.status === 401) {
        this.handleSessionExpired();
        return;
      }

      // For other errors (network, server), show reconnect notification
      this.showReconnectNotification();

      this.emitError({
        code: TELNYX_CONSTANTS.ERROR_CODES.AUTH_ERROR,
        message: 'Failed to refresh WebRTC credentials',
        details: error,
      });
    } finally {
      this.isRefreshingCredentials = false;
    }
  }

  /**
   * Handle complete session expiry - logout and redirect to login
   */
  private handleSessionExpired(): void {
    console.log('[TelnyxWebRTC] Session completely expired, logging out');
    this.isRefreshingCredentials = false;

    try {
      const notifier = this.injector.get(NotificationsService);
      notifier.showNonErrorSnackBar('Your session has expired. Please log in again.');
    } catch (e) {
      // Notification service not available, continue with logout
    }

    // Small delay to show the message before redirect
    setTimeout(() => {
      this.authService.logout();
    }, 1500);
  }

  /**
   * Show notification prompting user to refresh for call functionality
   */
  private showReconnectNotification(): void {
    try {
      const notifier = this.injector.get(NotificationsService);
      notifier.showNonErrorSnackBar(
        'Call connection lost. Please refresh the page to restore calling functionality.'
      );
    } catch (e) {
      console.error('[TelnyxWebRTC] Failed to show notification:', e);
    }
  }

  /**
   * Setup visibility change listener to detect wake from sleep.
   */
  private setupVisibilityChangeListener(): void {
    this.removeVisibilityChangeListener();

    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        this.handlePageVisible();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Remove visibility change listener.
   */
  private removeVisibilityChangeListener(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
      this.visibilityChangeHandler = null;
    }
  }

  /**
   * Handle page becoming visible (e.g., after laptop wake).
   * Debounced to prevent multiple rapid checks.
   */
  private handlePageVisible(): void {
    const now = Date.now();
    if (now - this.lastVisibilityCheck < this.VISIBILITY_CHECK_DEBOUNCE_MS) {
      return;
    }
    this.lastVisibilityCheck = now;

    if (!this.connectionService.connected()) {
      this.refreshCredentialsAndReconnect();
    }
  }

  private cleanup(): void {
    // Unsubscribe
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
      this.authSubscription = null;
    }
    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
      this.connectionSubscription = null;
    }

    // Remove visibility listener
    this.removeVisibilityChangeListener();

    // Clear grace period timer
    if (this._answerGraceTimer) {
      clearTimeout(this._answerGraceTimer);
      this._answerGraceTimer = null;
    }
    this._answerGracePeriodActive = false;

    // Clear ring timeout timer
    this.clearRingTimeout();

    // Disconnect
    this.connectionService.disconnect();

    // Cleanup audio
    this.audioService.cleanup();

    // Reset state
    this._currentCall.set(null);
    this._callState.set('idle');
    this._isMuted.set(false);
    this.activeCalls.clear();
    this.isInitialized = false;
    this.isInitializing = false;

    // NOTE: Do NOT complete subjects here!
    // TelnyxIntegrationService is a singleton that subscribes once in its constructor.
    // If we complete the subjects, the subscription receives a complete notification
    // and will never receive new events when the user logs back in.
    // The subjects should persist for the lifetime of the app.
  }
}
